import { useState } from 'react';
import { ArrowLeftRight, RefreshCw, Search } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { AGENCY_TZ, api, formatDate } from '../api';
import { useClientSelection } from '../client-context';
import { clientOptions } from '../client-options';
import Dialog from '../components/Dialog';
import FilterPill from '../components/FilterPill';
import NewProjectDialog, { PHASE_STATUSES, PROJECT_PRIORITIES as PRIORITIES, PROJECT_STATUSES as STATUSES } from '../components/NewProjectDialog';
import QuickMenu, { QuickMenuItem } from '../components/QuickMenu';
import { ActionForm, label, useMutation } from '../components/Workflow';
import { useApi } from '../hooks';
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from '../components/Ui';
import { cleanTitle } from '../task-title';
import { TASK_STATUS_LABEL } from '../types';
import { TaskEditor } from './Tasks';

type Project = { id: number; client_id: number | null; client_name: string | null; name: string; project_type: string | null; status: string; priority: string | null; owner_name: string | null; due_date: string | null; phase_count: number; phases_completed: number; open_tasks: number; updated_at?: string };
type Phase = { id: number; phase_name: string; phase_order: number; status: string; notes: string | null; external_blocker: string | null; is_blocked: boolean; task_count: number; tasks_done: number };
type ProjectTask = { id: number; title: string; status: string };
type ProjectDetail = Project & { description: string | null; created_at: string | null; phases: Phase[]; tasks: ProjectTask[] };

/** Board columns: every open state and Completed. Cancelled gets a column only when one is listed. */
const BOARD = ['planning', 'active', 'blocked', 'on_hold', 'completed'];
const VIEWS = ['board', 'list', 'timeline'] as const;
const statusTone = (status: string) => (status === 'blocked' ? 'danger' : status === 'completed' ? 'success' : 'neutral');
const phaseTone = (status: string) => (status === 'blocked' ? 'danger' : status === 'completed' ? 'success' : status === 'active' ? 'info' : 'neutral');
const PHASE_DONE = ['completed', 'skipped'];

/** Today on the agency's calendar, whoever is looking. */
const agencyToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: AGENCY_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
/** An open project past its due date. */
const isLate = (project: Project) => Boolean(project.due_date) && !['completed', 'cancelled'].includes(project.status) && String(project.due_date).slice(0, 10) < agencyToday();
/** "Oct 7", plus the year only when it is not this year. */
function shortDate(value: string) {
  const day = value.slice(0, 10);
  const date = new Date(`${day}T12:00:00Z`);
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC', ...(day.slice(0, 4) !== agencyToday().slice(0, 4) ? { year: 'numeric' } : {}) }).format(date);
}

export default function AgencyProjects() {
  const projects = useApi<Project[]>('/api/projects');
  const { clients } = useClientSelection();
  const [view, setView] = useState<(typeof VIEWS)[number]>('board');
  const [query, setQuery] = useState(''), [status, setStatus] = useState('all'), [client, setClient] = useState('all');
  const [creating, setCreating] = useState(false), [busy, setBusy] = useState(false), [feedback, setFeedback] = useState('');
  const all = projects.data || [];
  const needle = query.trim().toLowerCase();
  const rows = all.filter((project) =>
    (status === 'all' || project.status === status) &&
    (client === 'all' || (client === 'agency' ? !project.client_id : String(project.client_id || '') === client)) &&
    `${project.name} ${project.client_name || ''} ${project.owner_name || ''}`.toLowerCase().includes(needle));
  const filtered = Boolean(needle) || status !== 'all' || client !== 'all';
  const clearFilters = () => { setQuery(''); setStatus('all'); setClient('all'); };
  // An open project or task lives in the URL, so a link can open it and Back closes it.
  const [params, setParams] = useSearchParams();
  const openProject = params.get('project');
  const openTask = params.get('task');
  const setOpen = (changes: Record<string, number | null>) => setParams((current) => {
    const next = new URLSearchParams(current);
    for (const [key, id] of Object.entries(changes)) { if (id === null) next.delete(key); else next.set(key, String(id)); }
    return next;
  });
  const open = (project: Project) => setOpen({ project: project.id });

  async function move(project: Project, next: string) {
    setBusy(true); setFeedback('');
    try { await api(`/api/projects/${project.id}`, { method: 'PUT', body: JSON.stringify({ status: next }) }); projects.reload(); setFeedback(`${project.name} moved to ${label(next)}.`); }
    catch (error) { setFeedback(error instanceof Error ? error.message : 'Could not move the project.'); }
    finally { setBusy(false); }
  }

  const newProject = <button className="button" onClick={() => setCreating(true)}>New project</button>;
  const columns = rows.some((project) => project.status === 'cancelled') ? [...BOARD, 'cancelled'] : BOARD;
  const dated = rows.filter((project) => project.due_date).sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));
  const undated = rows.length - dated.length;

  return <>
    <PageHeader eyebrow="Delivery" title="Projects" actions={newProject} />
    <div className="tabs ap-views" role="tablist" aria-label="Project views">{VIEWS.map((value) => <button key={value} role="tab" aria-selected={view === value} className={view === value ? 'active' : ''} onClick={() => setView(value)}>{label(value)}</button>)}</div>
    {/* The filters and what they show are one panel, whatever the view or state. */}
    <section className="panel ap-main">
      <div className="portfolio-controls ap-filters">
        <label className="search-control"><Search aria-hidden="true" /><span className="sr-only">Search projects</span><input aria-label="Search projects" placeholder="Search projects or clients" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
        <FilterPill label="Client" value={client} onChange={setClient} options={[
          { value: 'all', name: 'All' },
          { value: 'agency', name: 'Agency (no client)' },
          ...clientOptions(clients.data).map((option) => ({ value: option.value, name: option.label })),
        ]} />
        <FilterPill label="Status" value={status} onChange={setStatus} options={[{ value: 'all', name: 'All' }, ...STATUSES.map((value) => ({ value, name: label(value) }))]} />
        <span className="toolbar-end">
          {filtered && <button type="button" className="text-button" onClick={clearFilters}>Clear filters</button>}
          {!!all.length && <span className="result-count" aria-live="polite">{rows.length} of {all.length} projects</span>}
          <button className="icon-button" aria-label="Refresh projects" title="Refresh projects" disabled={projects.refreshing} onClick={projects.reload}><RefreshCw className={projects.refreshing ? 'spin' : ''} aria-hidden="true" /></button>
        </span>
      </div>
      {feedback && <p role="status" className="inline-notice">{feedback}</p>}
      {projects.loading ? <LoadingState label="Loading projects" /> : projects.error ? <ErrorState message={projects.error} onRetry={projects.reload} />
        : !all.length ? <EmptyState title="No projects yet" description="Start a project to plan its phases and follow it here." action={newProject} />
        : !rows.length ? <EmptyState title="No matching projects" description="Try another search or filter." action={<button type="button" className="button secondary" onClick={clearFilters}>Clear filters</button>} />
        : view === 'board' ? (
          <div className="board-columns ap-board ap-project-board" data-columns={columns.length}>
            {columns.map((column) => {
              const items = rows.filter((project) => project.status === column);
              return <section className="board-column" key={column} aria-label={label(column)}>
                <h2>{label(column)}<span className="ap-count">{items.length}</span></h2>
                {items.map((project) => {
                  const late = isLate(project);
                  const client = project.client_name || 'Agency';
                  return <article className="board-card ap-card" key={project.id}>
                    <button type="button" className="ap-card-title" onClick={() => open(project)}>{project.name}</button>
                    <span className="ap-card-context" title={client}>{client}</span>
                    <div className="ap-card-meta">
                      <span className="ap-card-owner">{project.phase_count ? `${project.phases_completed} of ${project.phase_count} phases` : project.owner_name || 'No owner'}</span>
                      {project.due_date && <time className={late ? 'ap-card-due late' : 'ap-card-due'} dateTime={project.due_date.slice(0, 10)} title={late ? `Overdue since ${formatDate(project.due_date)}` : `Due ${formatDate(project.due_date)}`}>{late && <span className="sr-only">Overdue, </span>}{shortDate(project.due_date)}</time>}
                      <QuickMenu label={`Move ${project.name}`} align="right" disabled={busy} trigger={<span className="chip chip-ghost chip-sm ap-move" title="Change status"><ArrowLeftRight aria-hidden="true" /></span>}>
                        {(close) => STATUSES.map((value) => <QuickMenuItem key={value} active={value === project.status} onClick={() => { close(); if (value !== project.status) void move(project, value); }}>{label(value)}</QuickMenuItem>)}
                      </QuickMenu>
                    </div>
                  </article>;
                })}
                {!items.length && <p className="ap-empty">Nothing here</p>}
              </section>;
            })}
          </div>
        ) : view === 'list' ? (
          <div className="table-wrap ap-list">
            <table className="module-table">
              <thead><tr><th>Project</th><th>Client</th><th>Owner</th><th>Status</th><th>Priority</th><th className="num">Phases</th><th className="num">Open tasks</th><th>Due</th></tr></thead>
              <tbody>{rows.map((project) => <tr key={project.id} className="is-clickable" onClick={(e) => { if (!(e.target as HTMLElement).closest('a,button')) open(project); }}>
                <td data-label="Project"><button type="button" className="ap-row-open" onClick={() => open(project)}>{project.name}</button><small className="muted">{label(project.project_type || 'other')}</small></td>
                <td data-label="Client">{project.client_name || 'Agency'}</td>
                <td data-label="Owner">{project.owner_name || 'Unassigned'}</td>
                <td data-label="Status"><StatusBadge tone={statusTone(project.status)}>{label(project.status)}</StatusBadge></td>
                <td data-label="Priority">{label(project.priority || 'medium')}</td>
                <td data-label="Phases" className="num">{project.phase_count ? `${project.phases_completed} / ${project.phase_count}` : '—'}</td>
                <td data-label="Open tasks" className="num">{Number(project.open_tasks || 0)}</td>
                <td data-label="Due" className={isLate(project) ? 'ap-late' : undefined}>{formatDate(project.due_date)}</td>
              </tr>)}</tbody>
            </table>
          </div>
        ) : (
          <div className="ap-timeline">
            <h2>Deadlines</h2>
            {dated.length > 0 && <ol className="ap-timeline-list">{dated.map((project) => <li key={project.id}>
              <button type="button" className="timeline-row ap-timeline-row" onClick={() => open(project)}>
                <strong className={isLate(project) ? 'ap-late' : undefined}>{formatDate(project.due_date)}</strong>
                <span className="ap-timeline-name">{project.name}<small>{project.client_name || 'Agency'}</small></span>
                <StatusBadge tone={statusTone(project.status)}>{label(project.status)}</StatusBadge>
              </button>
            </li>)}</ol>}
            {undated > 0 && <p className="panel-note">{dated.length ? `${undated} more ${undated === 1 ? 'project has' : 'projects have'} no due date.` : 'None of these projects has a due date yet.'}</p>}
          </div>
        )}
    </section>
    {openProject && <ProjectPanel key={openProject} id={openProject} onClose={() => setOpen({ project: null })} onChanged={(message) => { projects.reload(); if (message) setFeedback(message); }} onOpenTask={(id) => setOpen({ project: null, task: id })} />}
    {openTask && <TaskEditor key={openTask} id={openTask} onClose={() => setOpen({ task: null })} onSaved={() => { setOpen({ task: null }); projects.reload(); }} />}
    {creating && <NewProjectDialog onClose={() => setCreating(false)} onCreated={(message) => { setCreating(false); projects.reload(); setFeedback(message); }} />}
  </>;
}

/**
 * One project, opened in place: its phases (each opens a small form to update it),
 * its tasks, and its status, priority and due date. Agency projects have no client
 * page, so this is where they are run.
 */
export function ProjectPanel({ id, onClose, onChanged, onOpenTask }: {
  id: string;
  onClose: () => void;
  /** After a save: reload the list, and show the message when there is one. */
  onChanged: (message?: string) => void;
  onOpenTask?: (taskId: number) => void;
}) {
  const project = useApi<ProjectDetail>(`/api/projects/${encodeURIComponent(id)}`);
  // Phase saves report at the top of the panel; the project's own Save reports beside its button.
  const save = useMutation();
  const saveProject = useMutation();
  const [phase, setPhase] = useState<Phase | null>(null);
  const p = project.data;
  const phases = p?.phases || [];
  const done = phases.filter((item) => PHASE_DONE.includes(item.status)).length;
  return (
    <Dialog title={p?.name || 'Project'} description={p ? p.client_name || 'Agency project' : undefined} onClose={() => { if (!save.busy && !saveProject.busy) onClose(); }}>
      {project.loading ? <LoadingState label="Loading project" />
        : project.error || !p ? <ErrorState title="Couldn’t open this project" message={project.error || 'It may have been deleted.'} onRetry={project.reload} />
        : phase ? (
          <div className="ap-phase-edit">
            <h3>{phase.phase_order}. {phase.phase_name}</h3>
            {phase.is_blocked && !PHASE_DONE.includes(phase.status) && <p className="workflow-note">An earlier phase is not finished yet. This one can be completed once that one is done or skipped.</p>}
            <ActionForm
              key={phase.id}
              busy={save.busy}
              submit="Save phase"
              onCancel={() => { setPhase(null); save.setNotice(''); }}
              fields={[
                { name: 'status', options: PHASE_STATUSES.includes(phase.status) ? PHASE_STATUSES : [...PHASE_STATUSES, phase.status], value: phase.status, hint: 'Completing or skipping a phase starts the phases waiting on it.' },
                { name: 'notes', type: 'textarea', value: phase.notes },
                { name: 'external_blocker', label: 'Waiting on (outside the agency)', type: 'textarea', value: phase.external_blocker },
              ]}
              onSubmit={async (data) => {
                const result = await save.run(`/api/projects/${p.id}/phases/${phase.id}`, data, 'PUT');
                if (result) { setPhase(null); project.reload(); onChanged(); save.setNotice(`${phase.phase_name} saved.`); }
              }}
            />
            {save.feedback}
          </div>
        ) : (
          <div className="ap-panel">
            {save.feedback}
            {p.description && <p className="ap-panel-description">{p.description}</p>}
            <dl className="ap-facts">
              <div><dt>Type</dt><dd>{label(p.project_type || 'other')}</dd></div>
              <div><dt>Owner</dt><dd>{p.owner_name || 'Unassigned'}</dd></div>
              <div><dt>Created</dt><dd>{formatDate(p.created_at)}</dd></div>
            </dl>
            <section className="ap-panel-section" aria-label="Phases">
              <h3>Phases{phases.length > 0 && <span className="ap-count">{done} of {phases.length} done</span>}</h3>
              {phases.length ? <ol className="ap-phases">{phases.map((item) => (
                <li key={item.id}>
                  <button type="button" className="ap-phase" onClick={() => { save.setNotice(''); setPhase(item); }} aria-label={`Update ${item.phase_name}`}>
                    <span className="ap-phase-order">{item.phase_order}</span>
                    <span className="ap-phase-name">{item.phase_name}
                      {item.external_blocker ? <small>Waiting on {item.external_blocker}</small> : item.is_blocked && !PHASE_DONE.includes(item.status) ? <small>After an earlier phase</small> : null}
                      {item.task_count > 0 && <small>{item.tasks_done} of {item.task_count} tasks done</small>}
                    </span>
                    <StatusBadge tone={phaseTone(item.status)}>{label(item.status)}</StatusBadge>
                  </button>
                </li>
              ))}</ol> : <p className="ap-empty">This project has no phases.</p>}
            </section>
            {(p.tasks || []).length > 0 && <section className="ap-panel-section" aria-label="Tasks">
              <h3>Tasks<span className="ap-count">{p.tasks.length}</span></h3>
              <ul className="ap-tasks">{p.tasks.map((task) => <li key={task.id}>
                {onOpenTask ? <button type="button" className="text-button" onClick={() => onOpenTask(task.id)}>{cleanTitle(task.title)}</button> : <span>{cleanTitle(task.title)}</span>}
                <span className="muted">{TASK_STATUS_LABEL[task.status] || label(task.status)}</span>
              </li>)}</ul>
            </section>}
            {p.client_id && <p className="ap-panel-link"><Link to={`/projects?client_id=${p.client_id}&record=${p.id}`}>Open in {p.client_name || 'the client'}’s projects</Link></p>}
            <ActionForm
              key={`${p.id}-${p.updated_at || ''}`}
              busy={saveProject.busy}
              submit="Save"
              onCancel={onClose}
              fields={[
                { name: 'status', options: STATUSES, value: p.status },
                { name: 'priority', options: PRIORITIES, value: p.priority || 'medium' },
                { name: 'due_date', label: 'Due date', type: 'date', value: p.due_date ? String(p.due_date).slice(0, 10) : '' },
              ]}
              onSubmit={async (data) => {
                const result = await saveProject.run(`/api/projects/${p.id}`, { status: data.status, priority: data.priority, due_date: data.due_date || null }, 'PUT');
                if (result) { onChanged(`${p.name} saved.`); onClose(); }
              }}
            />
            {saveProject.feedback}
          </div>
        )}
    </Dialog>
  );
}
